import { store } from '@/store';
import {
  printLettersData,
  printLettersFromRecord,
  selectPrintLetters
} from '@/utils/printLetter/printLetterConfig';

import { orgExtrasBase } from '@/services/emrAccessUsers';

import type {
  PrintLettersRecord,
  PrintLetterTemplate,
  ResolvedPrintLetters
} from '@/types/printLetter';

/** Dev-server stand-in for org-extras (vite.config.ts, VITE_PRINT_LETTER_LOCAL=true). */
export const LOCAL_PRINT_LETTER_BASE = '/__print-letter-local';

export const PRINT_LETTERS_RECORD = 'print-letters';

export function isPrintLetterLocal(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_PRINT_LETTER_LOCAL === 'true';
}

export function printLettersBase(): string {
  return isPrintLetterLocal() ? LOCAL_PRINT_LETTER_BASE : orgExtrasBase();
}

export class PrintLetterRequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PrintLetterRequestError';
    this.status = status;
  }
}

/** What staff see when a letter cannot be loaded. */
export function printLetterErrorMessage(error: unknown): string {
  const status = error instanceof PrintLetterRequestError ? error.status : 0;
  if (status === 404) return 'No print letter is set up for this clinic.';
  if (status === 502) return "Couldn't load the letter from PostGrid.";
  return error instanceof Error && error.message ? error.message : "Couldn't load the letter.";
}

function authHeaders(json = false): HeadersInit {
  const headers: Record<string, string> = {};
  const token = store.getState().auth.token;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

async function extrasFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const base = printLettersBase();
  if (!base) {
    throw new PrintLetterRequestError('Print letters are not available in this environment.', 0);
  }
  const response = await fetch(`${base}${path}`, {
    ...init,
    headers: { ...authHeaders(Boolean(init?.body)), ...(init?.headers ?? {}) }
  });
  const body = await response.text();
  let payload: unknown = {};
  try {
    payload = body ? JSON.parse(body) : {};
  } catch {
    payload = {};
  }
  if (!response.ok) {
    const message = (payload as { message?: unknown } | null)?.message;
    throw new PrintLetterRequestError(
      typeof message === 'string' && message
        ? message
        : `Print letter request failed (${response.status})`,
      response.status
    );
  }
  return payload as T;
}

const recordPath = (organizationId: string) =>
  `/organizations/${encodeURIComponent(organizationId)}/records/${PRINT_LETTERS_RECORD}`;

/** The org's own `print-letters` record, or null when it has none. */
export async function loadPrintLettersRecord(
  organizationId: string
): Promise<{ record: PrintLettersRecord; raw: Record<string, unknown> } | null> {
  try {
    const payload = await extrasFetch<unknown>(recordPath(organizationId));
    const record = printLettersFromRecord(payload);
    if (!record) return null;
    return { record, raw: printLettersData(payload) ?? {} };
  } catch (error) {
    if (error instanceof PrintLetterRequestError && error.status === 404) return null;
    throw error;
  }
}

export async function savePrintLettersRecord(
  organizationId: string,
  data: Record<string, unknown>
): Promise<void> {
  await extrasFetch(recordPath(organizationId), {
    method: 'PUT',
    body: JSON.stringify({ data })
  });
  clearPrintLettersCache();
}

/**
 * The clinic's record when that clinic has one. A record that is off, or has no letter on, stays
 * off. The parent account is used only when the clinic has no record.
 */
export async function resolvePrintLetters(
  clinicId: string,
  parentId?: string | null
): Promise<ResolvedPrintLetters | null> {
  if (!clinicId) return null;
  const clinic = await loadPrintLettersRecord(clinicId);
  const account =
    !clinic && parentId && parentId !== clinicId ? await loadPrintLettersRecord(parentId) : null;
  const selected = selectPrintLetters(clinic?.record ?? null, account?.record ?? null);
  if (!selected) return null;
  return {
    organizationId: selected.source === 'clinic' ? clinicId : parentId || '',
    source: selected.source,
    record: selected.record,
    letters: selected.letters
  };
}

const resolvedCache = new Map<string, Promise<ResolvedPrintLetters | null>>();

/** One lookup per clinic per page load. A failed lookup is not kept. */
export function loadPrintLettersForClinic(
  clinicId: string,
  parentId?: string | null
): Promise<ResolvedPrintLetters | null> {
  if (!clinicId || !printLettersBase()) return Promise.resolve(null);
  const key = `${clinicId}:${parentId ?? ''}`;
  const cached = resolvedCache.get(key);
  if (cached) return cached;
  const pending = resolvePrintLetters(clinicId, parentId).catch(() => {
    resolvedCache.delete(key);
    return null;
  });
  resolvedCache.set(key, pending);
  return pending;
}

export function clearPrintLettersCache(): void {
  resolvedCache.clear();
}

export async function loadPrintLetterTemplate(
  organizationId: string,
  letterId: string,
  inductive = false
): Promise<PrintLetterTemplate> {
  const device = inductive ? '?device=inductive' : '';
  const template = await extrasFetch<PrintLetterTemplate>(
    `/organizations/${encodeURIComponent(organizationId)}/print-letters/` +
      `${encodeURIComponent(letterId)}/template${device}`
  );
  if (!template || typeof template.html !== 'string' || !template.html.trim()) {
    throw new PrintLetterRequestError("Couldn't load the letter from PostGrid.", 502);
  }
  return template;
}
